import { type FormEvent, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { toast } from "react-toastify";

import TokenManager from "../../commons/TokenManager";
import { getAuthenticatedRedirectUrl, getFullPath } from "../../commons/helpers";
import { useAuthContext } from "../../contexts/Auth";
import { URL_RESET_PASSWORD } from "../../config";
import useUsers from "../../services/useUsers";

const theme = createTheme();
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPassword() {
    const navigate = useNavigate();
    const { requestPasswordRecovery } = useUsers();
    const { isAuthenticated } = useAuthContext();

    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            const tokenData = TokenManager.getTokenData();
            window.location.replace(getAuthenticatedRedirectUrl(Boolean(tokenData.validated)));
        }
    }, [isAuthenticated]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!email.trim()) {
            toast.error("Email e obrigatorio.");
            return;
        }

        if (!emailRegex.test(email.trim())) {
            toast.error("Informe um email valido.");
            return;
        }

        const success = await requestPasswordRecovery({
            email: email.trim(),
            loginPageUrl: getFullPath(URL_RESET_PASSWORD),
        });

        if (success) {
            setSubmitted(true);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: "blue" }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        Recuperar senha
                    </Typography>

                    {submitted ? (
                        <Box sx={{ mt: 3, textAlign: "center" }}>
                            <Typography sx={{ mb: 3 }}>
                                Caso o email esteja cadastrado em nossa base, voce recebera um link
                                com as instrucoes para recuperar a sua senha.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    navigate("/", { replace: true });
                                }}
                            >
                                Voltar para o login
                            </Button>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="email"
                                type="email"
                                autoComplete="email"
                                autoFocus
                                value={email}
                                onChange={(event) => {
                                    setEmail(event.target.value);
                                }}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                type="submit"
                            >
                                Enviar instrucoes
                            </Button>
                            {!email.trim() && (
                                <Link
                                    component={RouterLink}
                                    to="/"
                                    underline="hover"
                                    sx={{ display: "block", textAlign: "center" }}
                                >
                                    Pagina de login
                                </Link>
                            )}
                        </Box>
                    )}
                </Box>
            </Container>
        </ThemeProvider>
    );
}
